import { useEffect, useState, type ReactNode } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Loader2,
  LogOut,
  FileSpreadsheet,
  FolderOpen,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  Scale,
} from "lucide-react";
import { languoidEnglishName } from "@kolectra/shared/languoids";
import {
  ChallengeState,
  type ChallengeStateName,
} from "@kolectra/shared/states";
import { Permission } from "@kolectra/shared/permissions";
import type { ProjectDashboard, ProjectSyncStatus, ProjectSyncSource } from "@kolectra/shared/schemas";
import { dashboardApi, challengesApi, projectSyncApi, ApiError } from "../lib/api-client.js";
import { useAuth, useWebAuth, useWebPerms } from "../auth.js";
import { useProjectSurface } from "../project-surface.js";
import { PageHead } from "../components/page-head.js";
import { PreTestReviewDetail } from "../components/pre-test-review.js";

const STATE_LABEL: Record<ChallengeStateName, string> = {
  DRAFT: "Draft",
  READY_TO_RECORD: "To record",
  RECORDING: "Recording",
  READY_TO_TEST: "To test",
  TESTED: "Tested",
  JUDGED: "Judged",
  AWAITING_SECOND_REVIEW: "2nd review",
  DISPUTED: "Disputed",
  APPROVED: "Completed",
};

interface Metrics {
  total: number;
  approved: number;
  challengeSet: number;
  nonChallengeSet: number;
  byState: { state: string; count: number }[];
}

function StatTile({
  k,
  v,
  sub,
  color,
}: {
  k: string;
  v: number;
  sub: string;
  color?: string;
}) {
  return (
    <div className="stat">
      <div className="k">{k}</div>
      <div className="v" style={color ? { color } : undefined}>
        {v}
      </div>
      <div className="delta">{sub}</div>
    </div>
  );
}

function DashboardBody({
  total,
  approved,
  challengeSet,
  nonChallengeSet,
  byState,
}: Metrics) {
  return (
    <>
      <div className="statgrid">
        <StatTile k="Total challenges" v={total} sub="in scope" />
        <StatTile
          k="Completed"
          v={approved}
          sub="reached the end of the pipeline"
          color="var(--good)"
        />
        <StatTile
          k="Challenge sets"
          v={challengeSet}
          sub="model was defeated"
          color="var(--data)"
        />
        <StatTile
          k="Non-challenge"
          v={nonChallengeSet}
          sub="model passed both tasks"
        />
      </div>
      <div className="toolbar" style={{ marginTop: 26, marginBottom: 12 }}>
        <strong
          style={{ fontFamily: "var(--disp)", fontSize: 16, fontWeight: 500 }}
        >
          Pipeline breakdown
        </strong>
      </div>
      <div className="breakdown">
        {byState.filter((s) => s.count > 0).length === 0 ? (
          <span className="mini">No challenges in scope yet.</span>
        ) : (
          byState
            .filter((s) => s.count > 0)
            .map((s) => (
              <span key={s.state} className="bk">
                <span className={`statepill sp-${s.state}`}>
                  {STATE_LABEL[s.state as ChallengeStateName] ?? s.state}
                </span>
                <b>{s.count}</b>
              </span>
            ))
        )}
      </div>
    </>
  );
}

// ---- Observer (project) overview: figures, progress, cost ----

const fmtInt = (n: number) => n.toLocaleString("en-US");
const fmtUsd = (n: number) =>
  n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: n > 0 && n < 1 ? 4 : 2,
  });
const pct = (a: number, b: number) => (b > 0 ? Math.round((a / b) * 100) : 0);

const sheetHref = (id: string) =>
  `https://docs.google.com/spreadsheets/d/${id}/edit`;
const folderHref = (id: string) => `https://drive.google.com/drive/folders/${id}`;

/** A small external-link pill (reuses the breakdown chip style). */
function LinkChip({
  href,
  icon: Icon,
  label,
}: {
  href: string;
  icon: typeof FileSpreadsheet;
  label: string;
}) {
  return (
    <a
      className="bk"
      href={href}
      target="_blank"
      rel="noreferrer"
      style={{ textDecoration: "none", padding: "3px 9px", fontSize: 11, gap: 5 }}
    >
      <Icon size={12} strokeWidth={1.8} /> {label}
    </a>
  );
}

function SectionTitle({
  children,
  top = 26,
}: {
  children: ReactNode;
  top?: number;
}) {
  return (
    <div className="toolbar" style={{ marginTop: top, marginBottom: 12 }}>
      <strong
        style={{ fontFamily: "var(--disp)", fontSize: 16, fontWeight: 500 }}
      >
        {children}
      </strong>
    </div>
  );
}

/** A stat tile whose value is a fraction (value / of) with a progress bar. */
function ProgressTile({
  k,
  value,
  of,
  color,
}: {
  k: string;
  value: number;
  of: number;
  color?: string;
}) {
  const p = pct(value, of);
  return (
    <div className="stat">
      <div className="k">{k}</div>
      <div className="v">
        {fmtInt(value)} <small>/ {fmtInt(of)}</small>
      </div>
      <div className="bar">
        <i style={{ width: `${p}%`, ...(color ? { background: color } : {}) }} />
      </div>
      <div className="delta">{p}% uploaded</div>
    </div>
  );
}

interface BreakdownRow {
  key: string;
  label: string;
  total: number;
  approved: number;
  challengeSet: number;
  /** Challenge-set target (200 × languages) — drives the completion bar. */
  target: number;
  sheetUrl?: string | null;
  folderUrl?: string | null;
}

function BreakdownTable({
  caption,
  rows,
}: {
  caption: string;
  rows: BreakdownRow[];
}) {
  return (
    <div className="tablewrap">
      <table className="kolectra">
        <thead>
          <tr>
            <th>{caption}</th>
            <th>Total</th>
            <th>Completed</th>
            <th>Challenge sets</th>
            <th>Defeat rate</th>
            <th>Completion</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={6} className="mini">
                No data in scope.
              </td>
            </tr>
          ) : (
            rows.map((r) => (
              <tr key={r.key}>
                <td>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      flexWrap: "wrap",
                    }}
                  >
                    <span>{r.label}</span>
                    {r.sheetUrl && (
                      <LinkChip
                        href={r.sheetUrl}
                        icon={FileSpreadsheet}
                        label="Sheet"
                      />
                    )}
                    {r.folderUrl && (
                      <LinkChip
                        href={r.folderUrl}
                        icon={FolderOpen}
                        label="Audio"
                      />
                    )}
                  </div>
                </td>
                <td>{fmtInt(r.total)}</td>
                <td>{fmtInt(r.approved)}</td>
                <td>
                  {fmtInt(r.challengeSet)}{" "}
                  <span className="mini">/ {fmtInt(r.target)}</span>
                </td>
                <td className="mini">{pct(r.challengeSet, r.approved)}%</td>
                <td style={{ minWidth: 130 }}>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <div
                      className="bar"
                      style={{ flex: 1, marginTop: 0, minWidth: 46 }}
                    >
                      <i
                        style={{
                          width: `${pct(r.challengeSet, r.target)}%`,
                          background: "var(--data)",
                        }}
                      />
                    </div>
                    <span className="mini">
                      {pct(r.challengeSet, r.target)}%
                    </span>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The observer's comprehensive project view: headline figures, the pipeline
 * funnel, recording coverage (Drive uploads), Gemini test cost, and per-team /
 * per-language breakdowns. Cost is shown here (the project oversight surface)
 * but stays off the team-lead and judging surfaces.
 */
function ObserverOverview({ data }: { data: ProjectDashboard }) {
  const {
    total,
    approved,
    challengeSet,
    challengeSetTarget,
    nonChallengeSet,
    byState,
    cost,
    recording,
    perTeam,
    perLanguage,
  } = data;

  return (
    <>
      <div className="statgrid">
        <StatTile k="Total challenges" v={total} sub="in scope" />
        <div className="stat">
          <div className="k">Completed</div>
          <div className="v" style={{ color: "var(--good)" }}>
            {fmtInt(approved)}
          </div>
          <div className="bar">
            <i style={{ width: `${pct(approved, total)}%` }} />
          </div>
          <div className="delta">{pct(approved, total)}% of all challenges</div>
        </div>
        <div className="stat">
          <div className="k">Challenge sets</div>
          <div className="v" style={{ color: "var(--data)" }}>
            {fmtInt(challengeSet)} <small>/ {fmtInt(challengeSetTarget)}</small>
          </div>
          <div className="bar">
            <i
              style={{
                width: `${pct(challengeSet, challengeSetTarget)}%`,
                background: "var(--data)",
              }}
            />
          </div>
          <div className="delta">
            {pct(challengeSet, challengeSetTarget)}% to target ·{" "}
            {pct(challengeSet, approved)}% defeat
          </div>
        </div>
        <div className="stat">
          <div className="k">Test cost</div>
          <div className="v">{fmtUsd(cost.totalUsd)}</div>
          <div className="delta">
            {fmtInt(cost.runs)} runs · {fmtInt(cost.errors)} errors
          </div>
        </div>
      </div>

      <SectionTitle>Pipeline breakdown</SectionTitle>
      <div className="breakdown">
        {byState.filter((s) => s.count > 0).length === 0 ? (
          <span className="mini">No challenges in scope yet.</span>
        ) : (
          byState
            .filter((s) => s.count > 0)
            .map((s) => (
              <span key={s.state} className="bk">
                <span className={`statepill sp-${s.state}`}>
                  {STATE_LABEL[s.state as ChallengeStateName] ?? s.state}
                </span>
                <b>{s.count}</b>
              </span>
            ))
        )}
      </div>

      <SectionTitle>Recording coverage</SectionTitle>
      <div className="l-grid2">
        <ProgressTile
          k="Segments uploaded"
          value={recording.segmentsUploaded}
          of={total}
        />
        <ProgressTile
          k="Questions uploaded"
          value={recording.questionsUploaded}
          of={recording.questionsNeeded}
          color="var(--data)"
        />
      </div>

      <SectionTitle>Test cost detail</SectionTitle>
      <div className="breakdown">
        <span className="bk">
          Total <b>{fmtUsd(cost.totalUsd)}</b>
        </span>
        <span className="bk">
          Avg / run <b>{fmtUsd(cost.runs > 0 ? cost.totalUsd / cost.runs : 0)}</b>
        </span>
        <span className="bk">
          Tokens in <b>{fmtInt(cost.tokensIn)}</b>
        </span>
        <span className="bk">
          Tokens out <b>{fmtInt(cost.tokensOut)}</b>
        </span>
        <span className="bk">
          Outcome{" "}
          <b>
            {fmtInt(challengeSet)} set · {fmtInt(nonChallengeSet)} passed
          </b>
        </span>
      </div>

      <SectionTitle>By team</SectionTitle>
      <BreakdownTable
        caption="Team"
        rows={perTeam.map((t) => ({
          key: t.id,
          label: t.name,
          total: t.total,
          approved: t.approved,
          challengeSet: t.challengeSet,
          target: t.target,
        }))}
      />

      <SectionTitle top={20}>By language</SectionTitle>
      <BreakdownTable
        caption="Language"
        rows={perLanguage.map((l) => ({
          key: l.languoidCode,
          label: languoidEnglishName(l.languoidCode) ?? l.languoidCode,
          total: l.total,
          approved: l.approved,
          challengeSet: l.challengeSet,
          target: l.target,
          sheetUrl: l.spreadsheetId ? sheetHref(l.spreadsheetId) : null,
          folderUrl: l.driveFolderId ? folderHref(l.driveFolderId) : null,
        }))}
      />
    </>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <label
      className="mini"
      style={{ display: "flex", alignItems: "center", gap: 6 }}
    >
      {label}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ minWidth: 150 }}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * The browser is a PRE-TEST QC tool: only states before the model test appear.
 * Post-test work (judging, approval) lives on the Judgement page.
 */
const BROWSE_STATES: ChallengeStateName[] = [
  ChallengeState.DRAFT,
  ChallengeState.READY_TO_RECORD,
  ChallengeState.RECORDING,
  ChallengeState.READY_TO_TEST,
];

/**
 * Pre-test challenge browser (web) — for reviewers/admins holding the send-back
 * permissions (can_force_rework / can_reject_recording). A filterable list of the
 * team's pre-test challenges; selecting one opens the full design review with
 * recording statuses, the "created by" trail, and the two send-back actions
 * (rework → designer, reject audio → re-record). Model judging is NOT here — that
 * happens after testing, on the Judgement page. Lives in the Dashboard so the
 * overview and the work are one place.
 */
function ChallengeBrowser({
  teamId,
  langs,
}: {
  teamId: string;
  langs: string[];
}) {
  const [stateFilter, setStateFilter] = useState("ALL");
  const [langFilter, setLangFilter] = useState("");
  const [selectedId, setSelectedId] = useState<string | undefined>();

  const listQuery = useQuery({
    queryKey: ["challenges", teamId],
    queryFn: () => challengesApi.list(teamId),
  });
  const all = listQuery.data?.items ?? [];
  const filtered = all.filter(
    (c) =>
      BROWSE_STATES.includes(c.state) &&
      (stateFilter === "ALL" || c.state === stateFilter) &&
      (!langFilter || c.languoidCode === langFilter),
  );
  const filteredKey = filtered.map((c) => c.id).join(",");

  // Keep a valid selection as filters change.
  useEffect(() => {
    setSelectedId((cur) =>
      cur && filtered.some((c) => c.id === cur) ? cur : filtered[0]?.id,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredKey]);

  const reviewQuery = useQuery({
    queryKey: ["pre-test-review", selectedId],
    queryFn: () => challengesApi.preTestReview(selectedId!),
    enabled: !!selectedId,
  });

  return (
    <div style={{ marginTop: 30 }}>
      <div className="toolbar" style={{ marginBottom: 12 }}>
        <strong
          style={{ fontFamily: "var(--disp)", fontSize: 16, fontWeight: 500 }}
        >
          Pre-test review
        </strong>
        <div className="grow" />
        <FilterSelect
          label="State"
          value={stateFilter}
          onChange={setStateFilter}
          options={[
            { value: "ALL", label: "All states" },
            ...BROWSE_STATES.map((s) => ({ value: s, label: STATE_LABEL[s] })),
          ]}
        />
        {langs.length > 1 && (
          <FilterSelect
            label="Language"
            value={langFilter}
            onChange={setLangFilter}
            options={[
              { value: "", label: "All languages" },
              ...langs.map((c) => ({
                value: c,
                label: languoidEnglishName(c) ?? c,
              })),
            ]}
          />
        )}
      </div>

      <div className="l-split">
        <div className="tablewrap" style={{ background: "var(--panel)" }}>
          <div
            style={{
              padding: "10px 14px",
              borderBottom: "1px solid var(--hairline)",
              fontSize: 11,
              textTransform: "uppercase",
              letterSpacing: ".06em",
              color: "var(--ink-faint)",
              fontWeight: 600,
            }}
          >
            {filtered.length} challenge{filtered.length === 1 ? "" : "s"}
          </div>
          {listQuery.isLoading && (
            <div
              className="empty"
              style={{ display: "flex", alignItems: "center", gap: 8 }}
            >
              <Loader2 size={14} className="spin" /> Loading…
            </div>
          )}
          {listQuery.isError && (
            <div className="empty">Couldn’t load challenges.</div>
          )}
          {listQuery.isSuccess && filtered.length === 0 && (
            <div className="empty">No challenges match.</div>
          )}
          {filtered.map((c) => (
            <button
              key={c.id}
              onClick={() => setSelectedId(c.id)}
              style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                padding: "11px 14px",
                border: 0,
                borderBottom: "1px solid var(--hairline)",
                background: c.id === selectedId ? "#fff" : "transparent",
                cursor: "pointer",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                <span className="id">{c.segmentId}</span>
                <span
                  className={`statepill sp-${c.state}`}
                  style={{ marginLeft: "auto" }}
                >
                  {STATE_LABEL[c.state]}
                </span>
              </div>
              <div style={{ fontSize: 12.5, marginTop: 4 }}>{c.domain}</div>
            </button>
          ))}
        </div>

        {!selectedId ? (
          <div className="banner info">
            Select a challenge to review its design and recordings.
          </div>
        ) : reviewQuery.isLoading ? (
          <div
            className="banner info"
            style={{ display: "flex", alignItems: "center", gap: 8 }}
          >
            <Loader2 size={14} className="spin" /> Loading the challenge…
          </div>
        ) : reviewQuery.isError ? (
          <div className="banner bad">
            {reviewQuery.error instanceof ApiError
              ? reviewQuery.error.message
              : "Couldn’t load this challenge."}
          </div>
        ) : reviewQuery.data ? (
          <PreTestReviewDetail
            key={selectedId}
            review={reviewQuery.data}
            teamId={teamId}
          />
        ) : null}
      </div>
    </div>
  );
}

/**
 * Team dashboard (web) — progress overview for the active team, plus (for
 * reviewers/admins) a challenge browser to review, publish, or send back for
 * rework. A team lead sees only a language filter, and only when the team works
 * in more than one language.
 */
export function TeamDashboardPage() {
  const { account } = useWebAuth();
  const perms = useWebPerms();
  const teamId = account.team.id;
  const [lang, setLang] = useState("");

  // The browser is a pre-test QC tool; only the send-back permissions gate it.
  const canReview =
    perms.has(Permission.CAN_FORCE_REWORK) ||
    perms.has(Permission.CAN_REJECT_RECORDING);

  const q = useQuery({
    queryKey: ["dashboard", "team", teamId, lang],
    queryFn: () => dashboardApi.team(teamId, lang || undefined),
  });

  const langs = q.data?.languoidCodes ?? [];

  return (
    <>
      <PageHead
        crumb={`${account.team.name} · dashboard`}
        title="Team dashboard"
        lead="An overview of your team’s progress — and, for reviewers, the pre-test challenges to QC or send back."
      />
      <div className="pad">
        {langs.length > 1 && (
          <div className="toolbar" style={{ marginBottom: 18 }}>
            <FilterSelect
              label="Language"
              value={lang}
              onChange={setLang}
              options={[
                { value: "", label: "All languages" },
                ...langs.map((c) => ({
                  value: c,
                  label: languoidEnglishName(c) ?? c,
                })),
              ]}
            />
            {q.isFetching && (
              <Loader2
                size={14}
                className="spin"
                style={{ color: "var(--ink-faint)" }}
              />
            )}
          </div>
        )}
        {q.isError ? (
          <div className="banner bad">Couldn’t load the dashboard.</div>
        ) : !q.data ? (
          <div
            className="mini"
            style={{ display: "flex", alignItems: "center", gap: 8 }}
          >
            <Loader2 size={14} className="spin" /> Loading…
          </div>
        ) : (
          <DashboardBody {...q.data} />
        )}

        {canReview && <ChallengeBrowser teamId={teamId} langs={langs} />}
      </div>
    </>
  );
}

/**
 * Project dashboard (web) — for an project-level observer with no team. Read-only
 * progress across the whole project, filterable by team and language ("all" by
 * default). Rendered in its own minimal shell (the observer has no team nav).
 */
export function ProjectDashboardPage() {
  const { session, signOut } = useAuth();
  const projectSurface = useProjectSurface();
  const [teamId, setTeamId] = useState("");
  const [lang, setLang] = useState("");

  const q = useQuery({
    queryKey: ["dashboard", "project", teamId, lang],
    queryFn: () =>
      dashboardApi.project({
        teamId: teamId || undefined,
        languoidCode: lang || undefined,
      }),
  });

  const name = session?.user.displayName ?? session?.user.email ?? "";
  const initials = name
    ? name
        .split(/\s+/)
        .slice(0, 2)
        .map((p) => p[0])
        .join("")
        .toUpperCase()
    : "??";

  return (
    <div className="app">
      <div className="titlebar">
        <div className="lights">
          <i />
          <i />
          <i />
        </div>
        <div className="wordmark">
          Kolec<b>t</b>ra
        </div>
        <span className="sub">web · project</span>
        <div style={{ flex: 1 }} />
        {projectSurface?.hasWork && (
          <button
            onClick={() => projectSurface.setSurface("work")}
            title="Open Judgement"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 7,
              background: "#22252c",
              border: "1px solid #34373f",
              borderRadius: 20,
              color: "#e9ebef",
              padding: "5px 12px",
              cursor: "pointer",
              fontSize: 12.5,
              fontFamily: "var(--body)",
              marginRight: 10,
            }}
          >
            <Scale size={14} strokeWidth={2} /> Judgement
          </button>
        )}
        <SyncWidget />
        <div className="teampill" style={{ cursor: "default" }}>
          <div
            className="flag"
            style={{ background: "linear-gradient(135deg,#9a4a2e,#c2502b)" }}
          >
            {initials}
          </div>
          <span className="cc">
            {name} · {session?.project?.name ?? "Observer"}
          </span>
        </div>
        <button
          className="iconbtn"
          title="Sign out"
          onClick={() => void signOut()}
          style={{ marginLeft: 8 }}
        >
          <LogOut strokeWidth={1.8} />
        </button>
      </div>
      {/* The observer has no left nav, so the frame is a single full-width
          column (the team shell's `.frame` is 218px sidebar + 1fr main). */}
      <div className="frame" style={{ gridTemplateColumns: "1fr" }}>
        <div className="main">
          <PageHead
            crumb="Project · dashboard"
            title="Project dashboard"
            lead={
              <>
                A read-only, comprehensive view of the project — figures,
                pipeline progress, recording coverage, and Gemini test cost.
                Filter by team and language — <b>all</b> by default.
              </>
            }
          />
          <div className="pad">
            <div className="toolbar" style={{ marginBottom: 18, gap: 10 }}>
              <FilterSelect
                label="Team"
                value={teamId}
                onChange={setTeamId}
                options={[
                  { value: "", label: "All teams" },
                  ...(q.data?.teams ?? []).map((t) => ({
                    value: t.id,
                    label: t.name,
                  })),
                ]}
              />
              <FilterSelect
                label="Language"
                value={lang}
                onChange={setLang}
                options={[
                  { value: "", label: "All languages" },
                  ...(q.data?.languoidCodes ?? []).map((c) => ({
                    value: c,
                    label: languoidEnglishName(c) ?? c,
                  })),
                ]}
              />
              {q.isFetching && (
                <Loader2
                  size={14}
                  className="spin"
                  style={{ color: "var(--ink-faint)" }}
                />
              )}
            </div>
            {q.isError ? (
              <div className="banner bad">Couldn’t load the dashboard.</div>
            ) : !q.data ? (
              <div
                className="mini"
                style={{ display: "flex", alignItems: "center", gap: 8 }}
              >
                <Loader2 size={14} className="spin" /> Loading…
              </div>
            ) : (
              <ObserverOverview data={q.data} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Observer "Sync now" control (top-right of the title bar). Clicking it runs every
 * team's enabled Drive sources (server-side, as the system actor — same as the desktop
 * sync button / the scheduler), then opens a small log with the next auto-sync time.
 */
function SyncWidget() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const statusQ = useQuery({
    queryKey: ["project-sync"],
    queryFn: () => projectSyncApi.status(),
    refetchInterval: 60_000, // keep the "next auto-sync" countdown fresh
  });

  const run = useMutation({
    mutationFn: () => projectSyncApi.run(),
    onSuccess: (data) => {
      qc.setQueryData(["project-sync"], data);
      void qc.invalidateQueries({ queryKey: ["dashboard"] }); // figures change after a sync
      setOpen(true); // reveal the result log
    },
  });

  const status = run.data ?? statusQ.data;
  const configured = status?.configured ?? true;

  return (
    <div style={{ position: "relative", marginRight: 10 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          background: "#22252c",
          border: "1px solid #34373f",
          borderRadius: 20,
          overflow: "hidden",
        }}
      >
        <button
          type="button"
          onClick={() => run.mutate()}
          disabled={run.isPending || !configured}
          title={configured ? "Sync all teams now" : "No Google service account configured on the server"}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 7,
            background: "transparent",
            border: 0,
            color: configured ? "#e9ebef" : "#6a6e77",
            padding: "5px 12px",
            cursor: run.isPending || !configured ? "default" : "pointer",
            fontSize: 12.5,
            fontFamily: "var(--body)",
          }}
        >
          {run.isPending ? <Loader2 size={14} className="spin" /> : <RefreshCw size={14} strokeWidth={2} />}
          {run.isPending ? "Syncing…" : "Sync"}
        </button>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          title="Sync log"
          style={{
            background: "transparent",
            border: 0,
            borderLeft: "1px solid #34373f",
            color: "#aeb2bb",
            padding: "5px 9px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
          }}
        >
          <ChevronDown
            size={14}
            strokeWidth={2}
            style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .15s" }}
          />
        </button>
      </div>

      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
          <SyncLogPanel status={status} loading={statusQ.isLoading} error={run.error} />
        </>
      )}
    </div>
  );
}

function SyncLogPanel({
  status,
  loading,
  error,
}: {
  status?: ProjectSyncStatus;
  loading: boolean;
  error: unknown;
}) {
  return (
    <div
      style={{
        position: "absolute",
        top: "calc(100% + 8px)",
        right: 0,
        zIndex: 41,
        width: 340,
        maxWidth: "90vw",
        background: "var(--panel)",
        color: "var(--ink)",
        border: "1px solid var(--panel-edge)",
        borderRadius: 10,
        boxShadow: "0 12px 32px rgba(0,0,0,.18)",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "10px 14px",
          borderBottom: "1px solid var(--hairline)",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <span style={{ fontWeight: 600, fontSize: 12.5 }}>Drive sync</span>
        <span className="mini" style={{ marginLeft: "auto" }}>
          {nextAutoLabel(status)}
        </span>
      </div>

      {error ? (
        <div className="banner bad" style={{ margin: 10 }}>
          {error instanceof ApiError ? error.message : "Sync failed."}
        </div>
      ) : null}

      <div style={{ maxHeight: 320, overflowY: "auto" }}>
        {loading && !status ? (
          <div className="mini" style={{ padding: 14, display: "flex", gap: 8, alignItems: "center" }}>
            <Loader2 size={14} className="spin" /> Loading…
          </div>
        ) : !status || status.sources.length === 0 ? (
          <div className="mini" style={{ padding: 14 }}>
            No language sources configured yet.
          </div>
        ) : (
          status.sources.map((s) => <SyncLogRow key={s.teamId + s.languoidCode} s={s} />)
        )}
      </div>

      {status && !status.configured && (
        <div
          className="mini"
          style={{ padding: "8px 14px", borderTop: "1px solid var(--hairline)", color: "var(--bad)" }}
        >
          No Google service account configured on the server.
        </div>
      )}
    </div>
  );
}

function SyncLogRow({ s }: { s: ProjectSyncSource }) {
  const r = s.lastResult;
  return (
    <div
      style={{
        padding: "9px 14px",
        borderBottom: "1px solid var(--hairline)",
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12.5 }}>
        {s.lastError ? (
          <AlertTriangle size={13} style={{ color: "var(--bad)", flex: "none" }} />
        ) : s.lastSyncAt ? (
          <CheckCircle2 size={13} style={{ color: "var(--good)", flex: "none" }} />
        ) : (
          <span style={{ width: 13, flex: "none" }} />
        )}
        <span style={{ fontWeight: 500 }}>{s.languageName}</span>
        <span className="mini">· {s.teamName}</span>
        <span className="mini" style={{ marginLeft: "auto" }}>
          {relTime(s.lastSyncAt)}
        </span>
      </div>
      {s.lastError ? (
        <span className="mini" style={{ color: "var(--bad)" }}>
          {s.lastError}
        </span>
      ) : r ? (
        <span className="mini">
          +{r.created} created · {r.challengesCompleted} completed · {r.rowErrors} row errors
        </span>
      ) : !s.enabled ? (
        <span className="mini">disabled</span>
      ) : !s.hasSpreadsheet ? (
        <span className="mini">no spreadsheet set</span>
      ) : (
        <span className="mini">not synced yet</span>
      )}
    </div>
  );
}

/** Relative time like "2m ago" / "in 12m" / "just now". */
function relTime(iso: string | null): string {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const past = diff >= 0;
  const m = Math.round(Math.abs(diff) / 60000);
  if (m < 1) return past ? "just now" : "now";
  if (m < 60) return past ? `${m}m ago` : `in ${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return past ? `${h}h ago` : `in ${h}h`;
  const d = Math.round(h / 24);
  return past ? `${d}d ago` : `in ${d}d`;
}

function nextAutoLabel(status?: ProjectSyncStatus): string {
  if (!status) return "";
  if (!status.schedulerEnabled) return "auto-sync off";
  if (!status.nextAutoSyncAt) return "no auto-sync scheduled";
  return `next auto-sync ${relTime(status.nextAutoSyncAt)}`;
}
